<template>
<div class="chapter">
<div class="inner_page">
<div id="Chapter-Three.html" xml:lang="en-US">
<div class="Basic-Text-Frame-28">
<p class="ch"><a id="Anchor-1"></a>3</p>
<p class="sub"><em class="italic">Los Angeles<br/>December 1941</em></p>
<p class="body-text-opening"><span class="caps-epub" xml:lang="en-US">Every day when
					</span>the noon bell rang, it was the lunch monitor’s job to stand at the front
					of the class and choose rows of students to line up, the quietest and most
					attentive first.</p>
<p class="body-text">The teacher, for whom the ritual had lost some of its appeal
					over time—understandably, because she was at least a hundred years old—attended
					to her own tasks: gathering her purse and her lunch in its wicker pail, removing
					her glasses and placing them in the desk drawer, straightening stacks of papers.
					Unless the lunch monitor was utterly devoid of any sense of drama, she would
					drag out the selection, taking her time surveying the rows of eighth graders,
					and only after building sufficient suspense would she announce her choice.</p>
<p class="body-text"><em class="italic">Row three, you may line up.</em></p>
<p class="body-text">And then the process would be repeated until everyone had lined
					up for lunch.</p>
<p class="body-text">Each Monday morning, new recess and lunch monitors took up the
					yoke of duty, the schedule having been posted the first day of school. Lucy had
					waited more than three months for her turn. She had asked her mother to press
					her best blouse, the one with the tiny pleated ruffles around the Peter Pan
					collar. She had worn her favorite headband, the navy velvet with the small
					folded bow, and new snow-white socks. Lucy looked her best this Monday morning,
					and because she was Lucy Takeda, that meant she looked splendid indeed.</p>
<p class="body-text">All through the morning she waited impatiently, forcing herself
					not to slouch in her seat. At last it was nearly noon. The teacher glanced up at
					the clock, and then looked thoughtfully at Lucy. She did not smile. Instead she
					closed her eyes and pinched the flabby skin between her eyebrows, frowning as
					though she had a headache. Then she opened her planner and ran her finger down
					the page. “The new hall monitor this week shall be Samuel McGinnis,” she said
					without inflection. “The new lunch monitor shall be Nancy Marks.”</p>
<p class="body-text">For a second, Lucy was sure that she had heard wrong, that the
					teacher had made a mistake. Lucy had certainly not made a mistake—the date had
					been circled on the calendar at home for months.</p>
<p class="body-text">Nancy Marks turned in her seat and gawped at Lucy, but she
					scrambled to her feet when the teacher snapped that she didn’t have all day. It
					seemed that Nancy’s voice held a note of apology as she chose Lucy’s row to go
					first, but as the students filed to the front of the room, Nancy did not look at
					her.</p>
<p class="tb">* * *</p>
<p class="body-text-no-tab">“It’s because you’re a Jap,” Yvonne Graziano said, not
					without sympathy. Yvonne and Lucy had been best friends since second grade. They
					huddled in the corner of the playground under an arbor covered with the canes of
					climbing roses gone dormant for the winter. Lucy had learned not to stand too
					close, or her angora coat would get stuck on the thorns.</p>
<p class="body-text">Yvonne spoke with authority, since her eldest brother was in
					the Army Air Corps. He was stationed at March Field, but Yvonne’s mother was
					worried that he would be sent to the front lines as soon as the United States
					entered the war.</p>
<p class="body-text">“My dad says if there was ever a war with Japan, he’d sign up
					if they let him,” Lucy said, fighting back tears. She’d managed to stay proud
					and aloof all through lunch, though she had little appetite for the boiled egg
					and apple her mother had packed. “He says he’d go fight if he could.”</p>
<p class="body-text">Yvonne nodded sympathetically. “My dad says your dad is one of
					the good ones. But he’s too old.”</p>
<p class="body-text">It was true—Lucy’s father was astonishingly old. His teeth were
					long and yellow, and his mustache was more silver than black. Behind his shiny
					round spectacles his eyes—though kind, always kind—were nested in wrinkles.</p>
<p class="body-text">“But still, he’s as American as anyone else.” On this point
					Lucy was less certain, because her father still spoke Japanese occasionally. He
					read the <em class="italic">Rafu Shimpo,</em> a newspaper printed only in
					Japanese, and conducted much of his personal business in the shops along First
					Street in Little Tokyo. On their anniversary, her father took her mother to
					dinner at the Empire Hotel; he often brought her flowers wrapped in white paper
					from Uyehara Florist. Even their church, Christ Community Presbyterian, was
					mostly filled with Japanese families on Sundays.</p>
<p class="body-text">Still, Lucy had no doubts about her father’s patriotism. On the
					Fourth of July he studded the yard with tiny American flags, and he stood
					proudly for the national anthem at Gilmore Field when he took Lucy to see the
					Stars play.</p>
<p class="body-text">Yvonne looked at her sympathetically. “That’s good. But my dad
					says it’s not going to matter much longer, if Japan keeps invading. He says
					things are bound to change.”</p>
<p class="body-text">Yvonne’s words were as chilling as they were vague. <em class="italic">Change</em> was unimaginable. Lucy had grown up in the same
					house her parents lived in before she was born, a white two-story on Clement
					Street with black shutters and a porch with flowers spilling out of baskets
					hanging from the eaves, a nicer house than most of her friends lived in. Lucy
					had always had the same bedroom, the same bathroom with its pink-and-black tile
					and ruffled curtains in the window. The same walk to school—down Clement to the
					corner, crossing Normandie, and then three blocks to 156th—since the first day
					of kindergarten. The only changes in her life were the coverlets her mother made
					for her bed, the dresses hanging in her closet and the height of the two little
					twisty-branched trees in the front, which her mother had planted when she and
					her father were first married. Each year, they grew a few more inches, and Lucy
					knew that someday the tallest branches would reach the eaves.</p>
<p class="body-text">Lucy knew that her father was worried too, though he refused to
					speak of the war while Lucy was in the room; when her parents listened to the
					radio after dinner, she was sent to her room to study. Of course, she snuck out
					and listened, anyway. And there were the newspapers: she couldn’t read a single
					word of the <em class="italic">Rafu Shimpo,</em> but the headlines at the
					newsstand on the way to the market were impossible to miss. Hidden Tank Army
					Protects Moscow. Seven Vessels Sunk Off Italy.<em class="italic"> </em>Still, how could the events unfolding in these far-off places possibly
					affect Lucy and her family a million miles away in California, where even now,
					in the middle of winter, the air was scented with citrus blossoms?</p>
<p class="body-text">Two boys kicked a ball past them, coattails flapping. When they
					saw Lucy and Yvonne, the shorter of the two skidded to a halt. “Thought you were
					supposed to be lunch monitor this week,” he said, sticking a finger into his ear
					and scratching vigorously.</p>
<p class="body-text">Lucy couldn’t bear to look at him. Instead, she pretended to
					rub at a bit of dirt on the lid of her lunch pail.</p>
<p class="body-text">“Thought <em class="italic">you</em> were supposed to be
					running home to your mama,” Yvonne snapped. “I heard her calling you. She said
					you wet the bed again.”</p>
<p class="body-text">Lucy, buoyed by her friend’s loyalty, blinked and smiled shyly.
					But as the boy ran off and Yvonne linked an arm through hers, Lucy knew that the
					changes had already started, and nothing in her power could stop them.</p>
<p class="Basic-Paragraph-25"></p>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ChapterThree.vue?68f54070